/* eslint-disable complexity */
import dayjs from 'dayjs';
import * as _ from '@baidu/bce-i18n';
import {Modal} from '@/utils/escapeAntd';
import {
    PERSON_CHANGE_STEP,
    QUALIFY_STATUS,
    TIME_FORMATE,
    ChangeStateKey,
    ChangeTypeKey,
} from '@/dicts/qualify';
import client from '@/api/client';
import {QualifyInfo} from '@/types/api/qualify';
import {NewMainBody, OriginalMainBody, ChangeInfo} from '@/types/api/change';
import {getUserOutletByDevice} from '@/utils/util';
import {QualifyQueryParams} from '@/types/params';
import {ChangeAgreementType, ChangeWay} from '@/dicts/change';
import {getCurrentHost} from '@/modules/Qualify/common/util';

function originStatusCheck(origin: OriginalMainBody | null, changeType: ChangeWay) {
    if (changeType === 'CORPORATION_REAL_CHANGE') {
        switch (origin?.status) {
            case 'AUDIT':
                return PERSON_CHANGE_STEP.ORIGIN_FILE;
            case 'SUBMIT':
                return PERSON_CHANGE_STEP.ORIGIN_FACE;
            case 'HUMAN_AUDIT':
                return PERSON_CHANGE_STEP.ORIGIN_AUDIT;
        }
    }
    if (origin?.status === 'PASS') {
        return 'PASS';
    }
    else if (origin?.status === 'RETURN') {
        return PERSON_CHANGE_STEP.ORIGIN_RETURN;
    }
    else if (origin?.status === 'WITHDRAW') {
        return PERSON_CHANGE_STEP.WITHDRAW;
    }
    else if (changeType !== 'CORPORATION_REAL_CHANGE' && origin?.status === 'AUDIT') {
        return PERSON_CHANGE_STEP.ORIGIN_AUDIT;
    }
    return PERSON_CHANGE_STEP.ORIGIN_INIT;
}

function newStatusCheck(newMainBody: NewMainBody | null) {
    if (newMainBody?.status === 'PASS') {
        return PERSON_CHANGE_STEP.NEW_PASS;
    } else if (newMainBody?.status === 'RETURN') {
        return PERSON_CHANGE_STEP.NEW_RETURN;
    } else if (newMainBody?.status === 'AUDIT') {
        return PERSON_CHANGE_STEP.NEW_AUDIT;
    } else if (newMainBody?.status === 'WITHDRAW') {
        return PERSON_CHANGE_STEP.WITHDRAW;
    }
    return PERSON_CHANGE_STEP.ORIGIN_PASS;
}

export function checkInChangeProcess(
    changeInfo: any,
    params: any
) {
    const {change, current} = params;
    const status = changeInfo?.status;
    if (change && current && status) {
        const type = change === 'p2p' ? 'person' : change === 'p2c' ? 'company' : '';
        switch (status) {
            // 原主体认证通过，但是还停留在原主体阶段
            case PERSON_CHANGE_STEP.ORIGIN_PASS:
                if (current === 'origin') {
                    return `change/${type}`;
                }
                break;
            case PERSON_CHANGE_STEP.WITHDRAW:
                if (current === 'new') {
                    return 'change/result';
                }
                break;
            // 新主体审核中，原主体审核中 点击我已刷脸时跳转到认证结果页
            case PERSON_CHANGE_STEP.NEW_AUDIT:
            case PERSON_CHANGE_STEP.ORIGIN_AUDIT:
                return 'change/result';
            default:
                break;
        }
    }
    return '';
}

/**
 * 升级实名流程，发起变更提示
 */
export function showChangeWarning(info: QualifyInfo = {} as QualifyInfo, search: QualifyQueryParams) {
    let show = false;
    // 企业高级审核中
    if (info.hasPassCorporationPrime && info.company) {
        show = info.company.enterpriseSeniorStatus === QUALIFY_STATUS.AUDIT;
    }
    // 个人高级审核中
    if (info.hasPassPersonPrime && info.person) {
        show = info.person.personSeniorStatus === QUALIFY_STATUS.AUDIT;
    }
    const inChange = search?.change && search?.current;
    if (show && inChange) {
        return new Promise((resolve, reject) => {
            Modal.confirm({
                title: _('温馨提示'),
                content: _('您正处于实名认证的升级流程中，若仍坚持变更，您的实名升级将会被撤回。'),
                okText: _('仍坚持变更'),
                cancelText: _('放弃变更'),
                autoFocusButton: null,
                onOk: () => resolve(true),
                onCancel: () => {
                    reject(false);
                    location.href = getUserOutletByDevice();
                },
            });
        });
    }
    return Promise.resolve(true);
}

/**
 * 检查是否是原主体认证阶段
 * @param {*} current:当前变更流程，原主体还是新主体
 * @param {*} change:变更类型
 * @param {*} type:当前是哪类认证方式
 * @returns boolean
 */
export function checkChangeOrigin(
    current?: ChangeStateKey,
    change?: ChangeTypeKey,
    type?: 'person' | 'company' | 'enterprise'
) {
    switch (type) {
        // 个人变更，原主体认证不允许编辑
        case 'person':
            return current === 'origin' && change && ['p2c', 'p2p'].includes(change);
        // 企业变更企业新主体不允许编辑
        case 'company':
            return current === 'new' && change && ['c2c'].includes(change);
        default:
            return false;
    }
}

export function getChangeRejectReason(changeInfo: ChangeInfo, status: PERSON_CHANGE_STEP) {
    if (status === PERSON_CHANGE_STEP.ORIGIN_RETURN) {
        return changeInfo?.originalMainBody?.rejectReason;
    } else if (status === PERSON_CHANGE_STEP.NEW_RETURN) {
        return changeInfo?.newMainBody?.rejectReason;
    } else if (status === PERSON_CHANGE_STEP.WITHDRAW) {
        const origin = changeInfo?.originalMainBody?.rejectReason;
        const newReason = changeInfo?.originalMainBody?.rejectReason;
        const returnOrigin = origin && origin.length && origin[0];
        const returnNew = newReason && newReason.length && newReason[0];
        return returnOrigin ? origin : returnNew ? newReason : [];
    }
    return [];
}

// 当前原主体通过的时候，限制访问其他变更协议页面
export function checkOriginPassDiffNow(changeInfo: ChangeInfo, type: ChangeAgreementType, status: PERSON_CHANGE_STEP) {
    const originPass = [PERSON_CHANGE_STEP.NEW_RETURN, PERSON_CHANGE_STEP.ORIGIN_PASS].includes(status);
    const mainBodyType = changeInfo.originalMainBody?.mainBodyType?.toLocaleUpperCase();
    if (mainBodyType === 'PERSON') {
        const MAP = {
            CORPORATION: 'company',
            PERSON: 'person',
        };
        const newType = changeInfo.newMainBody?.mainBodyType?.toLocaleUpperCase();
        return originPass && MAP[newType as 'CORPORATION' | 'PERSON'] !== type;
    } else {
        return originPass && type !== 'enterprise';
    }
}

export function getSubmitSuccessUrl(current?: string, change?: string, expectUrl?: string) {
    let path = expectUrl || '/qualify/result';
    if (current === 'new') {
        path = '/qualify/change/result';
    } else {
        switch (change) {
            case 'p2p':
                path = '/qualify/change/person';
                break;
            case 'p2c':
                path = '/qualify/change/company';
                break;
            case 'c2c':
                path = '/qualify/change/enterprise';
                break;
        }
    }
    return path;
}

export function changeInstanceStatusCheck(info: ChangeInfo) {
    const status = originStatusCheck(info.originalMainBody, info.changeType);
    if (status !== 'PASS') {
        return status;
    }
    return newStatusCheck(info.newMainBody);
}

export function getChangeInfo() {
    return client.changeInfo().then(change => {
        change.status = changeInstanceStatusCheck(change);
        const originPassTime = change?.originalMainBody?.passTime;
        const newPassTime = change?.newMainBody?.passTime;
        change.originalMainBody = change.originalMainBody || ({} as any);
        if (originPassTime && change.originalMainBody) {
            change.originalMainBody.passTime = dayjs(originPassTime).format(TIME_FORMATE);
        }
        if (newPassTime && change.newMainBody) {
            change.newMainBody.passTime = dayjs(newPassTime).format(TIME_FORMATE);
        }
        return change;
    });
}

/**
 * 判断能否发起企业主体变更
 * @param ticketUrl
 * @param setLoading
 * @returns
 */
export function checkCompEntityChange(
    ticketUrl: string
) {
    return client.compChangeCheck().then(
        res => {
            if (res?.canInvoice && !res.disallowedChangeScenarios?.length) {
                return new Promise<boolean>((resolve, reject) => {
                    Modal.confirm({
                        title: _('温馨提示'),
                        autoFocusButton: null,
                        content: _('当前账号存在可开发票金额，若变更通过仅支持使用新主体开票'),
                        onOk: () => resolve(true),
                        onCancel: () => {
                            window.open(`${getCurrentHost()}/finance/invoice/list`, '_blank');
                            reject(false);
                        },
                        okText: _('我已知晓，继续提交'),
                        cancelText: _('去开发票'),
                    });
                });
            }
            // 非工商变更逻辑：有且仅包含 ‘原主体已注销或吊销’
            if (res?.disallowedChangeScenarios?.length && res.disallowedChangeScenarios[0] === '原主体已注销或吊销') {
                return Promise.resolve({changeType: 'revocation'} as any);
            }
            if (res?.disallowedChangeScenarios?.length) {
                return new Promise<boolean>((resolve, reject) => {
                    Modal.error({
                        title: _('温馨提示'),
                        closable: true,
                        autoFocusButton: null,
                        content: (
                            <div>
                                {_('当前账号存在以下场景，不允许提交企业主体变更，')}
                                {_('若有疑问请')}
                                <a href={ticketUrl} target="_blank" rel="noreferrer">
                                    {_('提交工单')}
                                </a>
                                {_('或联系客户经理')}
                                <div style={{whiteSpace: 'pre-line'}}>
                                    {res.disallowedChangeScenarios.map(
                                        (cur, idx) => <div key={cur}>{idx + 1}. {cur}</div>)}
                                </div>
                            </div>
                        ),
                        onOk: () => reject(new Error('')),
                        onCancel: () => reject(new Error('')),
                    });
                });
            }
            return Promise.resolve(true);
        },
        () => {
            return Promise.reject(false);
        }
    );
}

export function getNewRemindTime(changeInfo?: ChangeInfo) {
    if (changeInfo?.originalMainBody?.passTime) {
        const newWithdrawTime = dayjs(changeInfo?.originalMainBody?.passTime)
            .add(9, 'day').format('YYYY-MM-DD HH:mm:ss');
        return `您需要在${newWithdrawTime}前提交新主体实名认证，否则流程将自动终止`;
    }
    return '您需要在原主体通过后9天内提交新主体实名认证，否则流程将自动终止';
}
